'use client';

import {
  FC, PointerEvent, ReactNode, useRef,
} from 'react';

import dynamic from 'next/dynamic';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

import { GridGlobe } from './grid-globe';

const BackgroundGradientAnimation = dynamic(() => import('../background-gradient-animation'), {
  ssr: false,
});

const ContactSection = dynamic(() => import('./contact-section'), {
  ssr: false,
});

export type BentoGridItemVariant = 'default' | 'globe' | 'contact';

export interface BentoGridItemProps {
  id: number;
  className?: string;
  wrapperClassName?: string;
  titleClassName?: string;
  spareImgWrapperClassName?: string;
  variant?: BentoGridItemVariant;
  title?: string | ReactNode;
  description?: string | ReactNode;
  img?: string;
  imgClassName?: string;
  spareImg?: string;
}

export const BentoGridItem: FC<BentoGridItemProps> = ({
  className,
  wrapperClassName,
  titleClassName,
  spareImgWrapperClassName,
  variant = 'default',
  title,
  description,
  img,
  imgClassName,
  spareImg,
  id,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const reduced = useReducedMotion();

  const handlePointerEnter = () => {
    rectRef.current = rootRef.current?.getBoundingClientRect() ?? null;
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const el = rootRef.current;
    const rect = rectRef.current;

    if (!el || !rect) {
      return;
    }

    el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    el.style.setProperty('--my', `${event.clientY - rect.top}px`);
  };

  return (
    <motion.div
      ref={rootRef}
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      className={cn('relative overflow-hidden rounded-3xl border border-white/[0.1] group/bento transition-colors duration-300 hover:border-white/[0.22] flex flex-col space-y-4', className)}
      style={{ background: 'rgb(4,7,29)', backgroundImage: 'linear-gradient(90deg, rgba(4,7,29,1) 0%, rgba(12,14,35,1) 100%)' }}
      initial={{ opacity: 0, y: reduced ? 0 : 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: reduced ? 0.2 : 0.55, delay: reduced ? 0 : Math.min(id - 1, 5) * 0.07, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className={cn('h-full', wrapperClassName, {
          'flex justify-center relative': variant === 'contact',
        })}
      >
        {img && (
          <div className="w-full h-full absolute">
            <img
              src={img}
              alt=""
              className={cn(imgClassName, 'object-cover object-center')}
            />
          </div>
        )}

        {spareImg && (
          <div className={cn('absolute right-0 -bottom-5', spareImgWrapperClassName)}>
            <img
              src={spareImg}
              alt=""
              className="object-cover object-center w-full h-full"
            />
          </div>
        )}

        {/* Background artwork belongs to the tile, not the copy column: mounted inside the
            text column it only ever got that column's height (~160px on mobile). */}
        {variant === 'globe' && <GridGlobe />}

        {variant === 'contact' && (
          <BackgroundGradientAnimation>
            <div className="absolute z-50 inset-0 flex items-center justify-center text-white font-bold px-4 pointer-events-none text-3xl text-center md:text-4xl lg:text-7xl" />
          </BackgroundGradientAnimation>
        )}

        <div
          className={cn(
            titleClassName,
            'group-hover/bento:translate-x-2 transition duration-200 relative z-20 md:h-full min-h-40 flex flex-col px-5 p-5 lg:p-10',
          )}
        >
          {/* `relative` is load-bearing: without it the z-10 is inert and GridGlobe,
              which renders later in the DOM at z-10, paints over the copy. */}
          {description && (
            <div className="font-sans font-extralight md:max-w-48 md:text-xs lg:text-base text-sm text-[#C1C2D3] relative z-20">
              {description}
            </div>
          )}
          {title && (
            <div className="font-sans text-lg lg:text-3xl max-w-96 font-bold relative z-20">
              {title}
            </div>
          )}

          {variant === 'contact' && <ContactSection />}
        </div>
      </div>

      {/* Cursor bloom: LAST child at z-10 so it paints over the tile artwork (/b1.svg is
          an opaque full-bleed raster) but under the z-20 title column. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-300 group-hover/bento:opacity-100"
        style={{ background: 'radial-gradient(420px circle at var(--mx, 50%) var(--my, 50%), rgba(203,172,249,0.10), transparent 60%)' }}
      />
    </motion.div>
  );
};
